import { mergePortfolioData } from '@/service/ws/account-data-manager'
import type { PortfolioData } from '@/types/generated/common/account'

/**
 * The venue's own account total, `accountEquityUsd` (identical across every row
 * for an account — see the proto field comment). `totalEquityUsd` is never a
 * stand-in: it is per-currency/per-component (HL splits one account across
 * several same-currency rows), so any figure built from it is a partial total.
 * Undefined when no row carries the field — the venue can't value the account
 * right now, which is unknown, not zero.
 *
 * `pushedRows` is the rows of the WS push being handled; `mergedRows` is the
 * full post-merge row set for the account. The freshest value comes from the
 * push; a delta that carries none keeps the account's value from its other rows.
 */
export function resolveAccountEquityTotal(
  pushedRows: PortfolioData[],
  mergedRows: PortfolioData[] = pushedRows
): string | undefined {
  return (
    pushedRows.find((p) => p.accountEquityUsd)?.accountEquityUsd ??
    mergedRows.find((p) => p.accountEquityUsd)?.accountEquityUsd
  )
}

export type PortfolioFrame = {
  accountId: string
  rows: PortfolioData[]
  isInitialDump: boolean
}

/**
 * Applies one flush window's portfolio frames in arrival order: an initial dump
 * replaces its account's rows and keeps every other account's, a delta merges.
 * `totals` is each touched account's equity total as of its last frame, keyed
 * by account: '0' for a dump with no rows, null (unknown) when the account has
 * rows but none carries the venue's account total.
 */
export function applyPortfolioFrames(
  prev: PortfolioData[],
  frames: readonly PortfolioFrame[]
): { rows: PortfolioData[]; totals: Record<string, string | null> } {
  const totals: Record<string, string | null> = {}
  const rows = frames.reduce((acc, frame) => {
    const base = frame.isInitialDump
      ? acc.filter((b) => b.accountId && b.accountId !== frame.accountId)
      : acc
    const merged = mergePortfolioData(base, frame.rows)
    const accountRows = merged.filter((b) => !b.accountId || b.accountId === frame.accountId)
    // A dump with no balance rows at all is an account holding nothing: total 0,
    // not "no data yet" — left unset, the account cards wait on it and then drop it
    // as an "n of m accounts" gap. Rows without the venue's account total are the
    // venue saying it can't value the account: unknown, never 0.
    totals[frame.accountId] =
      frame.isInitialDump && accountRows.length === 0
        ? '0'
        : (resolveAccountEquityTotal(frame.rows, accountRows) ?? null)
    return merged
  }, prev)
  return { rows, totals }
}

/**
 * Fold every row of a latest-value account snapshot push into the by-account
 * record, last one winning per account.
 *
 * Takes the whole list rather than `rows[0]`. The server sends one row per
 * push today, but a single WS frame is allowed to carry several accounts:
 * `expand_account_scope` turns one `accountIds: [...]` subscription into one
 * stream per account, and public-api's outbound batcher coalesces same-variant
 * payloads inside its 250 ms window. Reading only the first row silently drops
 * accounts 2..N of any coalesced frame.
 *
 * `envelopeAccountId` is the fallback for a row whose own `accountId` is empty
 * (proto3 renders an unset string as ''), which is only correct while the frame
 * is single-account — so it is the fallback, never the primary.
 *
 * Returns `prev` unchanged when there is nothing to apply, so an empty push
 * cannot trigger a re-render.
 */
export function mergeAccountKeyedRows<T extends { accountId: string }>(
  prev: Record<string, T>,
  rows: readonly T[] | undefined,
  envelopeAccountId: string
): Record<string, T> {
  if (!rows?.length) return prev
  const next = { ...prev }
  for (const row of rows) {
    const id = row.accountId || envelopeAccountId
    if (!id) continue
    next[id] = row
  }
  return next
}
